// ---------------------------------------------------------------------------
// Concordancia de genero, plurais e numeros em PT-PT, para os textos da
// classificacao e dos grupos (porte do gramatica_pt.py do Cha139).
//
// O genero sai da primeira palavra do papel do elemento ("a viga", "o
// pilar", "a conduta", "o tubo electrico"). Os numeros formatam-se como o
// Python (`f"{x:.2f}"`, arredondamento ao par nos empates exactos), para
// os textos sairem iguais aos da analise de referencia.
// ---------------------------------------------------------------------------

const FEM_PRIMEIRA = new Set([
  'viga',
  'laje',
  'parede',
  'cobertura',
  'escada',
  'porta',
  'guarda',
  'luminária',
  'conduta',
  'tubagem',
  'curva',
])

const FORMAS = {
  o: ['o', 'a'],
  O: ['O', 'A'],
  do: ['do', 'da'],
  no: ['no', 'na'],
  pelo: ['pelo', 'pela'],
  ao: ['ao', 'à'],
  um: ['um', 'uma'],
  lo: ['lo', 'la'],
} as const

export type FormaDoArtigo = keyof typeof FORMAS

/** O papel e feminino? Decide pela primeira palavra. */
export function feminino(papel: string | null | undefined): boolean {
  const primeira = (papel ?? '').split(' ')[0].split('/')[0].toLowerCase()
  return FEM_PRIMEIRA.has(primeira)
}

/** Artigo ou contraccao concordante com o papel. */
export function artigo(papel: string, forma: FormaDoArtigo = 'o'): string {
  return FORMAS[forma][feminino(papel) ? 1 : 0]
}

/** Plural do papel: "pilar" da "pilares", "laje" da "lajes". */
export function plural(papel: string): string {
  const i = papel.indexOf(' ')
  const primeira = i < 0 ? papel : papel.slice(0, i)
  const resto = i < 0 ? '' : papel.slice(i)
  const p = primeira === 'pilar' ? 'pilares' : primeira === 'patamar' ? 'patamares' : `${primeira}s`
  return p + resto
}

/** Contagem com o plural certo: "1 elemento", "2 elementos". */
export function contagem(n: number, singular: string, pluralDe?: string): string {
  return `${n} ${n === 1 ? singular : (pluralDe ?? `${singular}s`)}`
}

/** Primeira letra em maiuscula. */
export function capitalizar(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

/** Arredondamento ao inteiro como o `round()` do Python: empates exactos
 *  ao par (124,5 da 124; 125,5 da 126). */
export function arredondarAoPar(x: number): number {
  const r = Math.round(x)
  if (Math.abs(x % 1) === 0.5) return r % 2 === 0 ? r : r - 1
  return r
}

/** `f"{x:.nf}"` do Python: casas fixas, empates exactos ao par. */
export function fixo(x: number, casas: number): string {
  const f = 10 ** casas
  const escalado = x * f
  // Empate exacto na representacao binaria (raro em dados reais).
  if (Math.abs(escalado % 1) === 0.5) {
    return (arredondarAoPar(escalado) / f).toFixed(casas)
  }
  return x.toFixed(casas)
}

/** Decimal com virgula: `dec(69.316, 2)` da "69,32". */
export function decimal(x: number, casas = 2): string {
  return fixo(x, casas).replace('.', ',')
}
